/**
 * Multi-application access control (June 2026).
 *
 * Single source of truth for the application catalogue + access resolution
 * used by User Management, the post-login router, and every workspace shell.
 * Add a future application by appending to APPLICATION_CATALOGUE.
 */
export const APP_WHX = "WarehouseX Transformation";
export const APP_SMARTEVALUATE = "SmartEvaluate";
export const APP_SMARTBID = "SmartBid";
export const APP_SMARTIMPROVE = "SmartImprove";
export const APP_SMARTEWM = "SmartEWM";
export const APP_SMARTINSPECT = "SmartInspect";

// Ordered catalogue — drives the multi-select checkbox list and badge order.
export const APPLICATION_CATALOGUE = [
  APP_WHX,
  APP_SMARTEVALUATE,
  APP_SMARTBID,
  APP_SMARTIMPROVE,
  APP_SMARTEWM,
  APP_SMARTINSPECT,
];

// WarehouseX Transformation is the full transformation platform — selecting it
// automatically grants access to every child application.
export const WHX_CHILD_APPS = [APP_SMARTEVALUATE, APP_SMARTBID, APP_SMARTIMPROVE, APP_SMARTEWM, APP_SMARTINSPECT];

// Apps that live INSIDE the consultant workspace shell (the others are
// standalone shells, e.g. SmartEWM at /app/smartewmbot).
export const CONSULTANT_WORKSPACE_APPS = [APP_SMARTEVALUATE, APP_SMARTBID, APP_SMARTIMPROVE, APP_SMARTINSPECT];

/**
 * Resolve the effective set of applications a user can access.
 * - Admin users implicitly have everything.
 * - WarehouseX Transformation expands to all child apps.
 * - Falls back to the legacy single `application_access` for un-migrated rows.
 */
export function effectiveApps(user) {
  if (!user) return [];
  if ((user.role || "").toLowerCase() === "admin") {
    return [...APPLICATION_CATALOGUE];
  }
  let apps = Array.isArray(user.applications) ? [...user.applications] : [];
  if (apps.length === 0 && user.application_access) {
    apps = [user.application_access];
  }
  const set = new Set(apps);
  if (set.has(APP_WHX)) {
    WHX_CHILD_APPS.forEach((a) => set.add(a));
  }
  return APPLICATION_CATALOGUE.filter((a) => set.has(a));
}

export function hasApp(user, app) {
  return effectiveApps(user).includes(app);
}

/** True when the user can access any app rendered inside the consultant shell. */
export function hasConsultantWorkspace(user) {
  return CONSULTANT_WORKSPACE_APPS.some((a) => hasApp(user, a));
}

export function hasSmartEwm(user) {
  return hasApp(user, APP_SMARTEWM);
}

export function hasSmartInspect(user) {
  return hasApp(user, APP_SMARTINSPECT);
}

export function hasSmartBid(user) {
  return hasApp(user, APP_SMARTBID);
}
